import type { TbEventWithId } from '@moment-golf/types';

export type ChallengeGroupDimension = 'division' | 'flight' | 'gender' | 'age';

export interface EnrichedChallengeSubmission {
    roundNumber: number;
    playerName: string;
    challengeName: string;
    challengeHole: number;
    value: unknown;
    division: string;
    flight: string;
    gender: string;
    age: number | null;
    /** EventScore document that stores this submission */
    eventScoreID: string;
    /** Index into that document's roundScores */
    roundIndex: number;
    submittedAt?: string | Date | null;
}

export interface ChallengeGroupPathEntry {
    dimension: ChallengeGroupDimension;
    label: string;
}

export interface ChallengeGroupNode {
    label: string;
    children?: ChallengeGroupNode[];
    submissions?: EnrichedChallengeSubmission[];
    /** Path from root to this node (for eligibility filtering) */
    groupPath?: ChallengeGroupPathEntry[];
}

export const UNASSIGNED_CHALLENGE_GROUP = 'Unassigned';

const UNASSIGNED = UNASSIGNED_CHALLENGE_GROUP;

const GENDER_ORDER = ['Male', 'Female', 'Other'];

export function getEnabledChallengeGroupDimensions(row: {
    byDivision?: boolean;
    byFlight?: boolean;
    byGender?: boolean;
    byAge?: boolean;
}): ChallengeGroupDimension[] {
    const enabled: ChallengeGroupDimension[] = [];
    if (row.byDivision === true) enabled.push('division');
    if (row.byFlight === true) enabled.push('flight');
    if (row.byGender === true) enabled.push('gender');
    if (row.byAge === true) enabled.push('age');
    return enabled;
}

export function getDimensionValueForReg(
    row: { division: string; flight: string; gender: string; age: number | null },
    dimension: ChallengeGroupDimension,
): string {
    switch (dimension) {
        case 'division':
            return row.division.trim() !== '' ? row.division : UNASSIGNED;
        case 'flight':
            return row.flight.trim() !== '' ? row.flight : UNASSIGNED;
        case 'gender':
            return row.gender.trim() !== '' ? row.gender : UNASSIGNED;
        case 'age':
            return row.age != null && Number.isFinite(row.age) ? String(row.age) : UNASSIGNED;
    }
}

function getDimensionValue(submission: EnrichedChallengeSubmission, dimension: ChallengeGroupDimension): string {
    return getDimensionValueForReg(submission, dimension);
}

export function getOrderedBucketLabelsFromValues(
    dimension: ChallengeGroupDimension,
    values: string[],
    event: TbEventWithId | null | undefined,
): string[] {
    const flighting = event?.flighting;
    const seenInData = new Set<string>(values);

    if (dimension === 'division') {
        const configured = (flighting?.divisions ?? []).map(
            (d: { divisionName?: string; name?: string }) => d.divisionName ?? d.name ?? String(d),
        );
        const ordered = configured.filter((name: string) => seenInData.has(name));
        if (seenInData.has(UNASSIGNED)) ordered.push(UNASSIGNED);
        const extras = Array.from(seenInData).filter(
            (name) => name !== UNASSIGNED && !ordered.includes(name),
        );
        return [...ordered, ...extras.sort()];
    }

    if (dimension === 'flight') {
        const configured = (flighting?.flights ?? []).map(
            (f: { flightName?: string; name?: string }) => f.flightName ?? f.name ?? String(f),
        );
        const ordered = configured.filter((name: string) => seenInData.has(name));
        if (seenInData.has(UNASSIGNED)) ordered.push(UNASSIGNED);
        const extras = Array.from(seenInData).filter(
            (name) => name !== UNASSIGNED && !ordered.includes(name),
        );
        return [...ordered, ...extras.sort()];
    }

    if (dimension === 'gender') {
        const ordered: string[] = [];
        for (const g of GENDER_ORDER) {
            if (seenInData.has(g)) ordered.push(g);
        }
        const extras = Array.from(seenInData)
            .filter((name) => name !== UNASSIGNED && !ordered.includes(name))
            .sort();
        ordered.push(...extras);
        if (seenInData.has(UNASSIGNED)) ordered.push(UNASSIGNED);
        return ordered;
    }

    const ages = Array.from(seenInData)
        .filter((name) => name !== UNASSIGNED)
        .sort((a, b) => Number(a) - Number(b));
    if (seenInData.has(UNASSIGNED)) ages.push(UNASSIGNED);
    return ages;
}

function getOrderedBucketLabels(
    dimension: ChallengeGroupDimension,
    submissions: EnrichedChallengeSubmission[],
    event: TbEventWithId | null | undefined,
): string[] {
    return getOrderedBucketLabelsFromValues(
        dimension,
        submissions.map((s) => getDimensionValue(s, dimension)),
        event,
    );
}

function buildGroupTree(
    submissions: EnrichedChallengeSubmission[],
    dimensions: ChallengeGroupDimension[],
    dimensionIndex: number,
    event: TbEventWithId | null | undefined,
    path: ChallengeGroupPathEntry[] = [],
): ChallengeGroupNode[] {
    if (dimensionIndex >= dimensions.length) {
        return [];
    }

    const dimension = dimensions[dimensionIndex];
    const bucketLabels = getOrderedBucketLabels(dimension, submissions, event);
    const isLeaf = dimensionIndex === dimensions.length - 1;
    const nodes: ChallengeGroupNode[] = [];

    for (const label of bucketLabels) {
        const bucketSubmissions = submissions.filter(
            (s) => getDimensionValue(s, dimension) === label,
        );
        if (bucketSubmissions.length === 0) continue;

        const nextPath = [...path, { dimension, label }];

        if (isLeaf) {
            nodes.push({ label, submissions: bucketSubmissions, groupPath: nextPath });
        } else {
            nodes.push({
                label,
                children: buildGroupTree(bucketSubmissions, dimensions, dimensionIndex + 1, event, nextPath),
                groupPath: nextPath,
            });
        }
    }

    return nodes;
}

export function groupChallengeSubmissions(
    submissions: EnrichedChallengeSubmission[],
    enabled: ChallengeGroupDimension[],
    event: TbEventWithId | null | undefined,
): ChallengeGroupNode[] {
    if (enabled.length === 0 || submissions.length === 0) {
        return [];
    }
    return buildGroupTree(submissions, enabled, 0, event, []);
}

export function sortChallengeSubmissions(
    submissions: EnrichedChallengeSubmission[],
    sortOrder: unknown,
): EnrichedChallengeSubmission[] {
    return [...submissions].sort((a, b) => {
        const aValue = Number(a.value);
        const bValue = Number(b.value);
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });
}
